import api from './api';
import clearUserData from './clearUserData';

// Access-token refresh shared by every request in the app (and every tab).
//
// The API rotates refresh tokens: each one can be redeemed exactly once. So when
// several requests 401 together after the access token expires, only one refresh
// may run; the rest wait for it and replay with the token it got. Tabs share
// localStorage, so the same applies across tabs (via a Web Lock).

const REFRESH_LOCK = 'ft-token-refresh';

let refreshInFlight = null;

const bearer = (token) => `Bearer ${token}`;

const adoptToken = (token) => {
    api.call.defaults.headers.common['Authorization'] = bearer(token);
    return token;
};

// Redeems the stored refresh token. Resolves to the new access token, or null when
// the session can't be renewed (in which case it has been cleared).
async function redeem(staleToken) {
    const current = localStorage.getItem('auth_token');
    const refreshToken = localStorage.getItem('refresh_token');

    // Another tab (or an earlier refresh in this one) already replaced the token
    // that failed â€” use that instead of spending the refresh token again.
    if (current && current !== staleToken) {
        return adoptToken(current);
    }
    if (!current || !refreshToken) {
        return null;
    }

    try {
        const response = await api.call.post('users/apiusers/refresh-token', {
            token: current,
            refreshToken
        });
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('refresh_token', response.data.refreshToken);
        localStorage.setItem('token_expiry', response.data.expiration);
        return adoptToken(response.data.token);
    } catch (err) {
        // Without Web Locks, another tab can redeem the same refresh token first
        // and this call loses. If that tab has saved its new token, use it.
        const latest = localStorage.getItem('auth_token');
        if (latest && latest !== current) {
            return adoptToken(latest);
        }
        console.error("Token refresh failed:", err);
        // The session is over â€” clear tokens and every cached org/user value.
        clearUserData();
        delete api.call.defaults.headers.common['Authorization'];
        return null;
    }
}

// staleToken: the access token that was rejected. Defaults to the stored one, for
// callers refreshing because it expired rather than because a request failed.
export function refreshAccessToken(staleToken = localStorage.getItem('auth_token')) {
    if (!refreshInFlight) {
        const run = () => redeem(staleToken);
        const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
        refreshInFlight = (locks ? locks.request(REFRESH_LOCK, run) : run())
            .finally(() => { refreshInFlight = null; });
    }
    return refreshInFlight;
}

const getAuthorization = (headers) =>
    (typeof headers?.get === 'function' ? headers.get('Authorization') : headers?.Authorization) || '';

// Handles a 401 from the API: refreshes once (shared with any concurrent 401s) and
// replays the request. The replay must carry the new token itself â€” a failed
// request's config already holds the expired header, which wins over the defaults.
// Rejects with the original error when the request can't be retried, calling
// onSessionExpired if that's because the session is over.
export async function retryWithRefreshedToken(error, onSessionExpired) {
    const request = error.config;
    // Never refresh on behalf of the refresh call itself (a rejected refresh token
    // would loop), and only retry a request once.
    if (!request || request._retry || request.url?.includes('refresh-token')) {
        return Promise.reject(error);
    }
    request._retry = true;

    const staleToken = getAuthorization(request.headers).replace(/^Bearer /, '');
    const token = await refreshAccessToken(staleToken);
    if (!token) {
        onSessionExpired();
        return Promise.reject(error);
    }

    if (typeof request.headers?.set === 'function') {
        request.headers.set('Authorization', bearer(token));
    } else {
        request.headers = { ...request.headers, Authorization: bearer(token) };
    }
    return api.call(request);
}
